<!--
  DropdownSelect.vue

  This Vue component is a custom dropdown select. 

  It expects the following required props:
    - An 'item' array of objects as options. Each object should have the following properties:
        - name: this will be used as text of option
        - value: this is optional. if not presented, by html default, value will
            identical to displayed text (which is equal to 'name')

  And the following optional props:
    - 'initialSelect' is a string that sets the initial selected item of the dropdown. If not provided, the default value is 'Please select one'.
    - 'noDisabled' is a boolean. If false, the initialSelect string WILL be shown in the dropdown menu.
    - 'id' and 'name' attributes can also be passed as props to set the id and name of the select element. If an id is not provided, a random one will be generated.  

  The template of the component consists of a div that acts as the dropdown, with an input field that displays the selected item. 
  The dropdown options are shown or hidden based on the 'showOptions' data property. 
  The 'toggleOptions' method is used to control the visibility of the options.
-->
<template>
  <div class="dropdown-select">
    <!-- Dropdown Display -->
    <div
      role="combobox"
      aria-controls="dropdown-options"
      aria-haspopup="listbox"
      :class="`dropdown-face ${showOptions ? 'show-options' : ''}`"
      :aria-expanded="showOptions"
      @click="toggleOptions()"
      v-click-outside="() => closeOptions()"
    >
      <input
        readonly
        type="text"
        class="non-readonly-style"
        :id="`${thisId}-face`"
        :disabled="disabled"
        :name="name ? name : thisId"
        :aria-label="initialSelect"
        v-model="selectedItem"
        :placeholder="initialSelect"
        @keydown="handleInputKeydown"
      />
      <svg>
        <use xlink:href="/assets/public/images/icons.svg#down-arrow" />
      </svg>
    </div>

    <!-- Dropdown Options -->
    <ul :id="thisId" class="dropdown-options" :disabled="disabled" v-show="showOptions" role="listbox" aria-label="dropdown-options">
      <!-- List item containing the placeholder text, shown if noDisabled is set to false -->
      <li disabled v-if="!noDisabled" role="option">
        <input disabled type="radio" class="non-input-style" />
        <label>{{ disabledDisplay }}</label>
      </li>

      <!-- List of dropdown options, set to a radio type for keyboard accessibility -->
      <li multiple v-for="(option, index) in items" :key="`${thisId}-${index}`" role="option">
        <input
          type="radio"
          class="non-input-style"
          :title="option.name"
          :key="index"
          :id="`${thisId}-${index}`"
          :name="name ? name : thisId"
          :value="option.value || option.value === 0 ? option.value : option.name"
          :disabled="disabled"
          :checked="index === selectedIndex()"
          @click="dropdownOptionHandler"
          @keydown="dropdownOptionHandler"
          @change.prevent
        />
        <!-- note that :for must match :id above for the dropdown click select to work -->
        <label :for="`${thisId}-${index}`">{{ isEmptyString(prefix) ? null : prefix }}{{ option.name || option.value }}</label>
      </li>
    </ul>
  </div>
</template>

<script>
import { v4 as uuid } from 'uuid';
import { isEmptyString } from '@general/js/utils';
import { CLICK_OUTSIDE } from '@general/js/_customDirectives';

export default {
  name: 'DropdownSelect',

  props: {
    v: {
      type: Object,
    },

    modelValue: {
      type: [String, Number],
    },

    initialSelect: {
      type: String,
      default: 'Please select one',
    },
    /**
     * Array of objects with `name` and `value` properties.
     */
    items: {
      type: Array,
      required: true,
    },

    id: {
      type: String,
    },

    name: {
      type: String,
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    noDisabled: {
      type: Boolean,
      default: true,
    },

    prefix: {
      required: false,
      type: String,
    },
  },

  directives: {
    'click-outside': CLICK_OUTSIDE,
  },

  data() {
    return {
      currentId: '',
      disabledDisplay: this.initialSelect,
      selectedItem: this.initialSelect,
      thisId: this.id ? this.id : uuid(),
      showOptions: false,
    };
  },

  computed: {
    selectedId: {
      get() {
        return this.currentId ? this.currentId : `${this.thisId}-${this.selectedIndex()}`;
      },
      set(newVal) {
        this.currentId = newVal;
      },
    },
  },

  methods: {
    selectedIndex() {
      let selectIndex = -1; // set to -1 so that first item is not automatically selected when item is not found
      this.items.forEach((item, index) => {
        if (item.name === this.selectedItem) selectIndex = index;
      });
      return selectIndex;
    },

    getName(value) {
      const selectedItem = this.items.find((item) => {
        return item.value === value;
      });
      return selectedItem ? selectedItem.name : null;
    },

    toggleOptions(isOpen) {
      // if isOpen is provided, set showOptions to isOpen. Otherwise, toggle showOptions
      if (isOpen) this.showOptions = isOpen;
      else this.showOptions = !this.showOptions;
    },

    closeOptions() {
      this.showOptions = false;
    },

    input($e) {
      // Set the selected item to the value of the option name, which is in the title attribute of the input
      this.selectedItem = $e.target.title;
      this.currentId = $e.target.id;

      // Emit the input of the option's value, which is in the value attribute of the input
      this.$emit('update:modelValue', $e.target.value);

      // eslint-disable-next-line no-unused-expressions
      if (this.v) this.v.$touch;

      // Close dropdown options
      this.toggleOptions(false);
    },

    // Handling keyboard events on the dropdown options menu
    dropdownOptionHandler(event) {
      if (event.type === 'keydown') {
        if (event.key === 'Enter') {
          this.input(event);
        } else if (event.key === 'Escape') {
          this.toggleOptions(false);
        } else if (event.key === 'Tab') {
          this.toggleOptions(false);
        }
      } else if (event.type === 'click' && event.screenX !== 0 && event.screenY !== 0) {
        // Detecting mouseclick event
        this.input(event);
      }
    },

    // Handling keyboard events on the dropdown face/input
    handleInputKeydown(event) {
      const selectedOption = document.getElementById(this.selectedId);
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          this.toggleOptions(true);
          selectedOption.focus();
          break;
        case 'ArrowUp':
          event.preventDefault();
          this.toggleOptions(true);
          selectedOption.focus();
          break;
        case 'Enter':
          this.toggleOptions();
          break;
        case 'Escape':
          this.toggleOptions(false);
          break;
        default:
          break;
      }
    },

    isEmptyString,
  },

  mounted() {
    if (this.modelValue) {
      this.selectedItem = this.getName(this.modelValue);
    }
  },

  watch: {
    modelValue(newValue) {
      const newItem = this.items.filter((item) => {
        return item.value === newValue;
      });
      this.selectedItem = newItem[0]?.name;
    },
    initialSelect(newValue) {
      this.selectedItem = newValue;
    },
  },
};
</script>